import React, { useEffect } from "react"
import { Row, Col, Card, Space, Flex, Button, Typography } from "antd"
import { EnvironmentOutlined, ClockCircleOutlined, StarFilled, TeamOutlined } from "@ant-design/icons"
import styles from "./PartnerPage.module.css"
import { Title } from "ui/Typography"
import { useTranslation } from "react-i18next"
import PropTypes from "prop-types"
import PhotoSlider from "components/common/PhotoSlider"
import Icon from "components/common/Icon"
import { useDispatch, useSelector } from "react-redux"
import { showModal } from "modules/modals/reducer"
import { oldRoutes } from "router/old-routes"
import { modelSelector } from "store/selectors"
import { cleanTrip } from "store/trips"
import { useNavigate } from "react-router-dom"
import { routes } from "router"

const TripsList = ({ trips }) => {
  const { t } = useTranslation()
  const stars = ["star-1", "star-2", "star-3", "star-4", "star-5"]
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const data = useSelector(modelSelector("trip"))
  const loggedIn = useSelector((store) => store.auth.loggedIn)

  const shareHandler = (id) =>
    dispatch(
      showModal("shareModal", {
        name: "Trip",
        path: routes.tripPath({ id })
      })
    )

  const handleBookNow = (uuid) => {
    if (loggedIn) navigate(routes.clientBookingWizardRootPath({ id: uuid }))
    else navigate({ pathname: oldRoutes.bookingWizardPath({ id: uuid }) })
  }

  useEffect(() => {
    if (data?.id) {
      dispatch(cleanTrip())
    }
  }, [data?.id, dispatch])

  return (
    <div className={styles.container}>
      <Title level={3} className="mb-xl">
        {t("partner.title")}
      </Title>
      <Row gutter={[24, 24]}>
        {trips?.data?.length === 0 ? (
          <div className={styles.container} style={{ padding: "48px 0", textAlign: "center" }}>
            <Typography.Title level={3}> {t("partner.no_trips")}</Typography.Title>
          </div>
        ) : (
          trips?.data?.map((trip) => {
            const {
              title,
              photos,
              cropped_photos,
              address,
              min_guests,
              max_guests,
              available_hours,
              pricing_type,
              price_per_person,
              flat_rate,
              uuid,
              minimum_rate
            } = trip || {}
            const { avatar, display_name } = trip?.guide || {}
            const price = pricing_type === "charge_per_person" ? min_guests * price_per_person : flat_rate
            const minRate = pricing_type === "set_minimum_rate" ? minimum_rate : ""
            return (
              <Col xs={24} sm={12} md={12} lg={8} key={uuid}>
                <Card hoverable className={styles.card}>
                  <div className="">
                    <div className="position-absolute top-0 start-0 w-100 p-3 z-2">
                      <div className="hstack gap-10">
                        <Button color="ghost" className="bg-white p-1" onClick={() => shareHandler(uuid)}>
                          <Icon iconName="Share" className="flex-shrink-0" size={18} block />
                        </Button>
                      </div>
                    </div>
                    <PhotoSlider photos={photos} cropped_photos={cropped_photos} preview className={`${styles.photoSlider} w-100 h-100`} />
                  </div>
                  <div className={styles.cardBody}>
                    <div>
                      <a className={styles.title} style={{ fontWeight: 500, marginBottom: 8 }} href={`/#/trips/${uuid}`}>
                        {title}
                      </a>
                      <Space direction="vertical" size="small" style={{ width: "100%" }}>
                        <Space align="start" size="small">
                          <EnvironmentOutlined />
                          <span wrap="wrap">{address}</span>
                        </Space>
                        <Space size="small">
                          <TeamOutlined />
                          <span>{`${min_guests} - ${max_guests} people`}</span>
                        </Space>
                        {available_hours?.length > 0 && (
                          <Space size="small" wrap>
                            <ClockCircleOutlined />
                            <span>
                              {available_hours?.map((slot) => (
                                <React.Fragment key={`${slot.start}-${slot.end}`}>
                                  {slot !== available_hours[0] && ", "}
                                  {slot.start} - {slot.end}
                                </React.Fragment>
                              ))}
                            </span>
                          </Space>
                        )}
                      </Space>
                      <Flex justify="space-between" align="center" wrap="wrap" style={{ marginTop: 12 }}>
                        <Space size="small">
                          <img src={avatar} alt="avatar" style={{ width: 24, height: 24, borderRadius: "50%" }} />
                          <span className={styles.guide}>{display_name}</span>
                          {stars?.map((star) => (
                            <StarFilled key={star} style={{ color: "#1677ff" }} />
                          ))}
                        </Space>
                        <Flex gap={6} align="center">
                          <span>from</span>
                          <span style={{ color: "#1677ff", fontWeight: 600, fontSize: 16 }}>$ {price ?? minRate}</span>
                        </Flex>
                      </Flex>
                      <div className={styles.bookNowBtn}>
                        <Button type="primary" block onClick={() => handleBookNow(uuid)}>
                          {t("partner.book_now")}
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              </Col>
            )
          })
        )}
      </Row>
    </div>
  )
}

export default TripsList

TripsList.propTypes = {
  trips: PropTypes.shape({
    data: PropTypes.array.isRequired
  }).isRequired
}
